<template>
  <div class="max-w-6xl mx-auto font-['Outfit',sans-serif] text-gray-800 min-h-screen">
    <!-- Header / Upload Section -->
    <div class="text-center mb-16">      
      <div v-if="!originalPreview" class="group relative max-w-2xl mx-auto">
        <input 
          type="file" 
          id="imageUpload" 
          @change="handleFileUpload" 
          accept="image/*" 
          class="hidden"
        />
        <label for="imageUpload" class="flex flex-col items-center justify-center border-2 border-dashed border-slate-200 rounded-[32px] p-12 bg-white hover:bg-blue-50/30 hover:border-blue-300 transition-all duration-500 cursor-pointer shadow-sm hover:shadow-xl hover:shadow-blue-500/5 group">
           <div class="w-20 h-20 bg-blue-50 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500">
              <Upload :size="32" class="text-blue-500" />
           </div>
           <h3 class="text-2xl font-bold text-slate-800 mb-2">Upload your photo</h3>
           <p class="text-slate-400 font-medium mb-8">Drag and drop or click to browse</p>
           
           <div class="inline-flex items-center gap-3 bg-blue-400 text-white px-10 py-4 rounded-2xl text-lg font-bold shadow-lg shadow-blue-500/20 group-hover:bg-blue-500 transition-all">
             Select Image
           </div>
        </label>
        
        <div class="mt-8 flex items-center justify-center gap-6">
           <div class="flex items-center gap-2 text-[10px] font-bold text-slate-400 uppercase tracking-widest bg-slate-50 px-4 py-2 rounded-full border border-slate-100">
              <Check :size="12" class="text-green-500" /> AI background removal
           </div>
           <div class="flex items-center gap-2 text-[10px] font-bold text-slate-400 uppercase tracking-widest bg-slate-50 px-4 py-2 rounded-full border border-slate-100">
              <Check :size="12" class="text-green-500" /> Standard sizes
           </div>
        </div>
      </div>
    </div>

    <!-- Main Processing Card -->
    <div v-if="originalPreview" class="bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden mt-8 transition-all">
      <div class="h-10 relative">
        <button @click="resetUpload" class="absolute right-5 top-5 text-gray-400 hover:text-gray-600 z-10">
          <X :size="24" />
        </button>
      </div>

      <div class="px-8 pb-8 pt-0">
        <div class="flex flex-col lg:flex-row gap-6 items-start">
          <!-- Original Column -->
          <div class="flex-1 w-full lg:w-auto">
            <h3 class="text-center font-bold text-lg mb-6">Original</h3>
            <div class="relative w-full rounded-lg overflow-hidden bg-gray-50 flex items-center justify-center border border-gray-100 group">
              <img :src="originalPreview" alt="Original" class="w-full h-auto block" />
              <div class="absolute top-3 right-3 flex gap-2">
                <button @click="openCropper" class="bg-black/70 text-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-black transition shadow-lg" title="Edit Crop">
                  <Crop :size="18" />
                </button>
              </div>
            </div>
          </div>

          <!-- Result Column -->
          <div class="flex-1 w-full lg:w-auto flex flex-col items-center">
            <h3 class="text-center font-bold text-lg mb-6">Result</h3>
            <!-- The white frame container -->
            <div class="bg-white p-2 border border-gray-100 shadow-sm flex items-center justify-center transition-all duration-300"
                 :style="{
                    width: '100%',
                    maxWidth: '350px',
                    aspectRatio: currentRatioStyle
                 }">
               <!-- The actual photo container -->
               <div class="relative w-full h-full overflow-hidden"
                    :class="[currentBgColor === 'transparent' ? 'result-checkerboard' : '']"
                    :style="{ 
                       background: currentBgColor === 'transparent' ? 'transparent' : currentBgColor
                    }">
                 <!-- Skeleton Loader for Result -->
                 <div v-if="loadingBg || loadingPassport" class="absolute inset-0 bg-gray-100 flex items-center justify-center z-20">
                   <div class="w-12 h-12 border-4 border-[#f0255a] border-t-transparent rounded-full animate-spin"></div>
                 </div>
                 
                 <img v-if="resultPreview && !loadingBg && !loadingPassport" :src="resultPreview" alt="Result" class="w-full h-full block" />
                 <div v-else-if="!resultPreview && !loadingBg && !loadingPassport" class="absolute inset-0 flex items-center justify-center text-gray-400">
                   <p>Processing...</p>
                 </div>
               </div>
            </div>
          </div>

          <!-- Sidebar Actions -->
          <div class="w-full lg:w-72 flex flex-col gap-4">
            <div class="hidden lg:block h-14"></div>
            
            <!-- Skeleton for Sidebar -->
            <div v-if="loadingBg || loadingPassport" class="space-y-3 animate-pulse">
              <div class="h-12 bg-gray-200 rounded-lg"></div>
              <div class="h-10 bg-gray-200 rounded-lg"></div>
              <div class="h-4 bg-gray-100 rounded-lg w-3/4 mx-auto"></div>
            </div>
            
            <template v-else>
              <button @click="openEditor" :disabled="loadingBg || loadingPassport || !resultPreview" class="w-full py-4 rounded-xl bg-slate-900 text-white font-bold hover:bg-slate-800 transition active:scale-[0.98] shadow-lg shadow-gray-200">
                Continue
              </button>
              
              <!-- New Format Select Style Dropdown -->
              <div class="relative" ref="downloadGroup">
                <div 
                  @click="toggleDropdown"
                  class="flex items-center justify-between w-full p-3 bg-white border border-gray-200 rounded-lg cursor-pointer hover:border-blue-400 transition group"
                >
                  <span class="font-medium flex items-center ml-5 gap-2">
                    <span v-if="selectedFormat === 'png'" class="text-slate-800">PNG</span>
                    <span v-else class="text-slate-800">JPG</span>
                  </span>
                  <ChevronDown :size="18" :class="['transition-transform duration-200', showDownloadDropdown ? 'rotate-180 text-blue-500' : 'text-gray-400']" />
                </div>

                <!-- Dropdown List -->
                <transition name="fade-slide">
                  <div v-if="showDownloadDropdown" class="absolute left-0 right-0 top-[calc(100%+8px)] bg-white border border-gray-100 rounded-xl shadow-2xl z-50 overflow-hidden py-1">
                    <div 
                      @click="selectFormat('jpg')"
                      class="flex items-center justify-center px-3 py-3 cursor-pointer transition relative"
                      :class="selectedFormat === 'jpg' ? 'bg-blue-50 text-blue-600 font-semibold' : 'hover:bg-gray-50'"
                    >
                      <span class="relative">
                        JPG
                        <Check v-if="selectedFormat === 'jpg'" :size="16" class="absolute left-full ml-5 top-1/2 -translate-y-1/2 text-blue-500" />
                      </span>
                    </div>
                    <div 
                      @click="selectFormat('png')"
                      class="flex items-center justify-center px-3 py-3 cursor-pointer transition border-t border-gray-50 relative"
                      :class="selectedFormat === 'png' ? 'bg-blue-50 text-blue-600 font-semibold' : 'hover:bg-gray-50'"
                    >
                      <span class="relative">
                        PNG
                        <Check v-if="selectedFormat === 'png'" :size="16" class="absolute left-full ml-5 top-1/2 -translate-y-1/2 text-blue-500" />
                      </span>
                    </div>
                  </div>
                </transition>

                <button @click="downloadResult" :disabled="!resultPreview" class="w-full mt-3 py-4 rounded-xl bg-blue-400 text-white font-bold text-lg shadow-lg shadow-blue-200 hover:bg-blue-500 transition active:scale-[0.98] flex items-center justify-center gap-2">
                   <Download :size="22" /> Download
                </button>
              </div>
              
              <p v-if="resultPreview" class="text-center text-xs text-gray-500 font-medium mt-3">Full Image 832 x 1248, 2 credits</p>

            </template>

            <!-- Interactive Rating Feedback -->
            <div v-if="resultPreview && !loadingBg && !loadingPassport" class="flex flex-col items-center justify-center gap-4 mt-10 transition-all duration-500">
              <transition name="fade-slide" mode="out-in">
                <div v-if="ratingSubmitted" :class="['flex items-center gap-2 px-8 py-2.5 rounded-full font-bold text-sm shadow-sm border', ratingType === 'good' ? 'bg-green-50 text-green-600 border-green-100' : 'bg-red-50 text-red-600 border-red-100']">
                   <div class="w-1.5 h-1.5 rounded-full animate-ping" :class="ratingType === 'good' ? 'bg-green-400' : 'bg-red-400'"></div>
                   Feedback Submitted!
                </div>
                <div v-else class="flex items-center justify-center gap-5 text-sm">
                  <span class="text-slate-400 font-bold uppercase tracking-widest text-[10px]">Rate this result</span>
                  <div class="flex gap-2">
                    <button @click="submitRating('good')" class="w-11 h-11 flex items-center justify-center rounded-full border border-slate-100 bg-white hover:border-green-400 hover:bg-green-50 text-slate-400 hover:text-green-600 transition-all active:scale-90 shadow-sm"><Smile :size="20" /></button>
                    <button @click="submitRating('bad')" class="w-11 h-11 flex items-center justify-center rounded-full border border-slate-100 bg-white hover:border-red-400 hover:bg-red-50 text-slate-400 hover:text-red-600 transition-all active:scale-90 shadow-sm"><Frown :size="20" /></button>
                  </div>
                </div>
              </transition>
            </div>
          </div>
        </div>
      </div>

      <div class="bg-gray-50 px-8 py-5 text-center border-t border-gray-100 shrink-0">
        <a href="#" class="inline-flex items-center gap-2 text-gray-800 font-bold text-sm hover:underline">
          <Wand2 :size="18" /> Professional Studio Photo Maker ›
        </a>
      </div>
    </div>

    <!-- Professional Cropper Dialogue (Teleported & Refined) -->
    <Teleport to="body">
      <transition name="fade">
        <div v-if="showCropper" class="fixed inset-0 z-[999999] flex items-center justify-center p-4">
          <div class="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" @click="cancelCrop"></div>
          
          <div class="relative bg-white w-full max-w-[900px] h-[650px] max-h-[90vh] rounded-3xl shadow-[0_40px_100px_-20px_rgba(0,0,0,0.6)] border border-gray-100 flex flex-col overflow-hidden animate-bounce-in">
            <!-- Header -->
            <div class="px-8 py-4 flex items-center justify-between bg-white border-b border-gray-100 shrink-0">
              <div class="flex items-center gap-3">
                <div class="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                  <Crop :size="18" class="text-blue-500" />
                </div>
                <h2 class="text-base font-bold text-slate-800 tracking-tight">Crop Image</h2> 
              </div>
              <div class="flex items-center gap-4">
                <button @click="confirmCrop" class="bg-blue-400 text-white px-10 py-2 rounded font-bold text-sm hover:bg-blue-500 transition active:scale-95 shadow-md">
                  Done
                </button>
                <button @click="cancelCrop" class="p-1.5 hover:bg-gray-100 rounded-md transition text-gray-400 hover:text-gray-900">
                  <X :size="20" />
                </button>
              </div>
            </div>

            <!-- Cropping Area -->
            <div class="flex-1 overflow-hidden bg-[#f8fafc] p-6">
              <div class="w-full h-full rounded-2xl overflow-hidden shadow-sm border border-gray-100">
                <VuePictureCropper
                    ref="cropperRef"
                    :boxStyle="{ width: '100%', height: 'calc(100% - 60px)', backgroundColor: '#fff', margin: '30px auto' }"
                    :img="masterPreview"
                    :options="{
                      viewMode: 1,
                      dragMode: 'move',
                      aspectRatio: currentRatio,
                      autoCropArea: 1,
                      background: true,
                      zoomable: true,
                      movable: true,
                      responsive: true,
                      guides: true,
                      center: true,
                      highlight: true,
                      modal: true,
                      minContainerHeight: 400
                    }"
                    @ready="onCropperReady"
                  />
              </div>
            </div>
          </div>
        </div>
      </transition>
    </Teleport>
 
    <!-- Professional Editor Modal (Connect to new Component) -->
    <PassportPhotoEditor 
      :show="showEditor" 
      :img="transparentPreview" 
      @close="showEditor = false"
      @confirm="confirmEditor"
      @change="handleEditorChange"
    />

    <!-- Error Message -->
    <div v-if="error" class="fixed bottom-5 right-5 bg-red-400 text-white px-6 py-4 rounded-xl shadow-2xl flex items-center gap-4 z-[2000] animate-bounce-in">
      <p class="font-medium">{{ error }}</p>
      <button @click="error = null" class="hover:bg-white/20 p-1 rounded-lg transition"><X :size="20" /></button>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import VuePictureCropper, { cropper } from 'vue-picture-cropper';
import PassportPhotoEditor from './PassportPhotoEditor.vue';
import { 
  Upload, 
  Crop, 
  X, 
  Download, 
  ChevronDown, 
  Smartphone, 
  Wand2, 
  Smile, 
  Frown,
  Check
} from 'lucide-vue-next';

export default {
  name: 'PassportPhotoMaker',
  components: {
    Upload, 
    Crop, 
    X, 
    Download, 
    ChevronDown, 
    Smartphone, 
    Wand2, 
    Smile, 
    Frown,
    Check,
    VuePictureCropper,
    PassportPhotoEditor
  },
  data() {
    return {
      selectedFile: null,
      masterFileSource: null,
      masterPreview: null,
      originalPreview: null,
      resultPreview: null,
      transparentPreview: null,
      loadingBg: false,
      loadingPassport: false,
      error: null,
      selectedFormat: 'png',
      showDownloadDropdown: false,
      showCropper: false,
      showEditor: false,
      currentBgColor: 'transparent',
      currentSize: null,
      currentRatio: NaN,
      ratingSubmitted: false,
      ratingType: null,
      savedCropData: null,
      apiBaseUrl: '/api-python', // Proxied API URL
    };
  },
  watch: {
    showCropper(val) {
      this.toggleBodyScroll(val || this.showEditor);
    },
    showEditor(val) {
      this.toggleBodyScroll(val || this.showCropper);
    }
  },
  methods: {
    toggleBodyScroll(lock) {
      if (lock) {
        document.body.classList.add('overflow-hidden');
      } else {
        document.body.classList.remove('overflow-hidden');
      }
    },
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      this.selectedFile = file;
      this.masterFileSource = file; // Store original source for non-destructive cropping
      this.originalPreview = URL.createObjectURL(file);
      this.masterPreview = URL.createObjectURL(file);
      
      this.resultPreview = null; // Clear previous results
      this.error = null;

      // Automatically trigger background removal
      this.removeBackground();
    },

    resetUpload() {
      this.selectedFile = null;
      this.masterFileSource = null;
      this.masterPreview = null;
      this.originalPreview = null;
      this.resultPreview = null;
      this.transparentPreview = null;
      this.savedCropData = null;
      this.error = null;
    },

    async removeBackground() {
      if (!this.selectedFile) return;
      
      this.loadingBg = true;
      this.error = null;
      
      const formData = new FormData();
      formData.append('image', this.selectedFile);

      try {
        const response = await axios.post(`${this.apiBaseUrl}/remove-bg`, formData, {
          responseType: 'blob' // Essential for binary data (images)
        });
        
        const transparentUrl = URL.createObjectURL(response.data);
        this.resultPreview = transparentUrl;
        this.transparentPreview = transparentUrl;
      } catch (err) {
        console.error(err);
        this.error = "Error removing background. Is the server running?";
      } finally {
        this.loadingBg = false;
      }
    },

    async convertToPassport() {
      if (!this.selectedFile) return;

      this.loadingPassport = true;
      this.error = null;

      const formData = new FormData();
      formData.append('image', this.selectedFile);

      try {
        const response = await axios.post(`${this.apiBaseUrl}/passport-photo`, formData, {
          responseType: 'blob'
        });
        
        const transparentUrl = URL.createObjectURL(response.data);
        this.resultPreview = transparentUrl;
        this.transparentPreview = transparentUrl;
      } catch (err) {
        console.error(err);
        this.error = "Error creating passport photo. Is the server running?";
      } finally {
        this.loadingPassport = false;
      }
    },

    openCropper() {
      if (!this.originalPreview) return;
      // We don't reset currentRatio here if we want to remember it, or we let the savedCropData handle it
      this.showCropper = true;
    },

    onCropperReady() {
      // Restore previously saved crop area if it exists
      if (this.savedCropData) {
        cropper.setData(this.savedCropData);
      }
    },

    setCropRatio(ratio) {
      this.currentRatio = ratio;
    },

    cancelCrop() {
      this.showCropper = false;
    },

    async confirmCrop() {
      try {
        // Save the crop data right before confirming, so it can be restored if opened again
        this.savedCropData = cropper.getData(true);
        
        const croppedBlob = await cropper.getBlob();
        if (!croppedBlob) return;

        // Convert current file name to avoid losing extension info
        const fileName = this.selectedFile ? this.selectedFile.name : 'cropped_image.jpg';
        this.selectedFile = new File([croppedBlob], fileName, { type: croppedBlob.type });
        
        // Update previews
        this.originalPreview = URL.createObjectURL(croppedBlob);
        this.resultPreview = null;
        
        this.showCropper = false;
        
        // Automatically re-trigger background removal for the cropped image
        await this.removeBackground();
      } catch (err) {
        console.error(err);
        this.error = "Failed to crop image.";
      }
    },

    openEditor() {
       if (!this.transparentPreview) return;
       this.showEditor = true;
    },

    confirmEditor(editedImg) {
       this.showEditor = false;
       this.resultPreview = editedImg; // Update the final result with edited image
    },

    handleEditorChange({ color, size }) {
       this.currentBgColor = color;
       this.currentSize = size;
    },

    toggleDropdown() {
      if (!this.resultPreview) return;
      this.showDownloadDropdown = !this.showDownloadDropdown;
    },

    selectFormat(format) {
      this.selectedFormat = format;
      this.showDownloadDropdown = false;
    },

    submitRating(type) {
      this.ratingSubmitted = true;
      this.ratingType = type;
      setTimeout(() => {
        this.ratingSubmitted = false;
        this.ratingType = null;
      }, 3000);
    },

    downloadResult() {
      if (!this.resultPreview) return;
      this.showDownloadDropdown = false; // Close popover on download
      
      const fileName = `passport_photo_${Date.now()}`;
      
      if (this.selectedFormat === 'png') {
        const link = document.createElement('a');
        link.href = this.resultPreview;
        link.download = `${fileName}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        // Convert to JPG (Add white background)
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          
          // High Quality Processing
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          
          // Fill background white
          ctx.fillStyle = "#FFFFFF";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          
          ctx.drawImage(img, 0, 0);
          
          // Use MAX quality (1.0) for JPEG
          const jpgUrl = canvas.toDataURL('image/jpeg', 1.0);
          const link = document.createElement('a');
          link.href = jpgUrl;
          link.download = `${fileName}.jpg`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        };
        img.src = this.resultPreview;
      }
    }
  },
  mounted() {
    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (this.$refs.downloadGroup && !this.$refs.downloadGroup.contains(e.target)) {
        this.showDownloadDropdown = false;
      }
    });
  }
};
</script>
<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;600;700&display=swap');

/* Keeping only animations and checkerboard that Tailwind doesn't handle natively */

.result-checkerboard {
  background-image: 
    linear-gradient(45deg, #eee 25%, transparent 25%), 
    linear-gradient(-45deg, #eee 25%, transparent 25%), 
    linear-gradient(45deg, transparent 75%, #eee 75%), 
    linear-gradient(-45deg, transparent 75%, #eee 75%);
  background-size: 16px 16px;
  background-position: 0 0, 0 8px, 8px -8px, -8px 0px;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

.fade-slide-enter-active, .fade-slide-leave-active {
  transition: all 0.2s ease;
}
.fade-slide-enter-from, .fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.animate-bounce-in {
  animation: bounce-in 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

@keyframes bounce-in {
  0% { transform: scale(0.9); opacity: 0; }
  100% { transform: scale(1); opacity: 1; }
}

/* --- Professional Cropper Styling Customization (Matches Competitor) --- */
:deep(.cropper-bg) {
  /* Slightly lighter checkerboard behind the image for better contrast */
  background-image: 
    linear-gradient(45deg, #e6e6e6 25%, transparent 25%), 
    linear-gradient(-45deg, #e6e6e6 25%, transparent 25%), 
    linear-gradient(45deg, transparent 75%, #e6e6e6 75%), 
    linear-gradient(-45deg, transparent 75%, #e6e6e6 75%) !important;
  background-size: 16px 16px !important;
  background-position: 0 0, 0 8px, 8px -8px, -8px 0px !important;
}

:deep(.cropper-modal) {
  /* Semi-transparent white to lightly dim the uncropped area instead of fully obscuring it */
  background-color: #ffffff !important;
  opacity: 0.65 !important;
}

:deep(.cropper-view-box) {
  /* Solid black outline around the crop area */
  outline: 1px solid #000 !important;
  outline-color: rgba(0, 0, 0, 0.8) !important;
}

:deep(.cropper-dashed) {
  /* Black solid grid lines instead of default dashed white */
  border-color: rgba(0, 0, 0, 0.8) !important;
  border-style: solid !important;
  opacity: 1 !important;
}

:deep(.cropper-line) {
  /* Hide the thick default white bounding lines */
  background-color: transparent !important;
}

:deep(.cropper-point) {
  /* Base style for all handles - black */
  background-color: #000 !important;
  opacity: 1 !important;
}

/* Corner handles as distinct black circles */
:deep(.cropper-point.point-nw),
:deep(.cropper-point.point-ne),
:deep(.cropper-point.point-sw),
:deep(.cropper-point.point-se) {
  width: 10px !important;
  height: 10px !important;
  border-radius: 50% !important;
}
:deep(.cropper-point.point-nw) { top: -5px !important; left: -5px !important; }
:deep(.cropper-point.point-ne) { top: -5px !important; right: -5px !important; }
:deep(.cropper-point.point-sw) { bottom: -5px !important; left: -5px !important; }
:deep(.cropper-point.point-se) { bottom: -5px !important; right: -5px !important; }

/* Edge handles as subtle black pill lines */
:deep(.cropper-point.point-n),
:deep(.cropper-point.point-s) {
  width: 16px !important;
  height: 4px !important;
  border-radius: 4px !important;
  left: 50% !important;
  transform: translateX(-50%) !important;
}
:deep(.cropper-point.point-n) { top: -2px !important; mt: 0 !important; }
:deep(.cropper-point.point-s) { bottom: -2px !important; mb: 0 !important; }

:deep(.cropper-point.point-e),
:deep(.cropper-point.point-w) {
  width: 4px !important;
  height: 16px !important;
  border-radius: 4px !important;
  top: 50% !important;
  transform: translateY(-50%) !important;
}
:deep(.cropper-point.point-e) { right: -2px !important; mr: 0 !important; }
:deep(.cropper-point.point-w) { left: -2px !important; ml: 0 !important; }
</style>e>